import { lazy, Suspense, type ReactNode } from 'react';
import { ChartBarIcon, DesktopIcon, GearIcon } from '@phosphor-icons/react';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/cms/ui/tabs';
import { Card, CardHeading } from '@/components/ui/card';
import { CardTitle } from '@/components/ui/text';

// Loaded when the Activity tab first opens
const AuditLog = lazy(() => import('@/components/project/audit-log').then(module => ({ default: module.AuditLog })));

/** A project page's tabs: its stories (the children), its analytics still to come, and its activity log. */
export function ProjectTabs({ children }: { children: ReactNode }) {
  return (
    <Tabs defaultValue="preview" className="w-full">
      <TabsList className="grid h-auto w-full grid-cols-3 p-1">
        <TabsTrigger value="preview" className="flex flex-col items-center gap-1 px-2 py-3 text-lureo-12 leading-4">
          <DesktopIcon className="h-5 w-5" />
          <span className="hidden sm:inline">Stories</span>
        </TabsTrigger>
        <TabsTrigger value="analytics" className="flex flex-col items-center gap-1 px-2 py-3 text-lureo-12 leading-4">
          <ChartBarIcon className="h-5 w-5" />
          <span className="hidden sm:inline">Analytics</span>
        </TabsTrigger>
        <TabsTrigger value="audit" className="flex flex-col items-center gap-1 px-2 py-3 text-lureo-12 leading-4">
          <GearIcon className="h-5 w-5" />
          <span className="hidden sm:inline">Activity</span>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="preview" className="mt-6 space-y-8">
        {/* Stories Section */}
        <div data-tour="project-stories-section" className="space-y-6">
          {children}
        </div>
      </TabsContent>

      <TabsContent value="analytics" className="mt-6">
        <Card elevation="high">
          <CardHeading icon={<ChartBarIcon className="text-lureo-ink-strong" />} title="Analytics" className="mb-6 min-h-7" />
          {/* A feature still to come: a large glyph, a card title and a line of body text */}
          <div className="py-12 text-center">
            <ChartBarIcon aria-hidden className="mx-auto mb-4 h-12 w-12 text-lureo-ink-subtle" />
            <CardTitle as="h3" className="mb-2 leading-7">Analytics Coming Soon</CardTitle>
            <p className="text-lureo-16 leading-6 text-lureo-ink-subtle">
              Track user engagement, completion rates, and story performance metrics.
            </p>
          </div>
        </Card>
      </TabsContent>

      <TabsContent value="audit" className="mt-6">
        <Card elevation="high">
          <CardHeading icon={<GearIcon className="text-lureo-ink-strong" />} title="Activity Log" className="mb-6 min-h-7" />
          <Suspense fallback={<LoadingSpinner size="lg" />}>
            <AuditLog />
          </Suspense>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
